import React, {useState} from 'react';
import {useAppContext} from '../context/AppContext';

const SignIn: React.FC = () => {
  const { signIn, isCheckingConnection, language, setLanguage, i18n, theme, setTheme } = useAppContext();
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    try {
      const res = await signIn(email.trim(), token.trim());
      setMessage(res?.message || i18n.t('connectionSuccess'));
    } catch (e: any) {
      const msg = e?.message ? String(e.message) : i18n.t('connectionFailed');
      setError(msg);
    }
  };

  const disabled = isCheckingConnection || !email || !token;

  return (
    <div className="signin-container">
      <div className="signin-split">
        <div className="signin-left" aria-hidden="true"/>
        <div className="signin-right">
          <form onSubmit={handleSubmit} className="signin-card">

            <div className="row" style={{justifyContent: 'space-between', alignItems: 'center'}}>
              <span className="logo-container">
                {/* use relative path so it works in web and electron */}
                <img src="logo.svg" alt="SNK Logo"/>
              </span>

              <div className="row" style={{gap: 8, alignItems: 'center'}}>
                {/* Theme toggle */}
                <button
                  type="button"
                  className="btn theme-toggle"
                  title={theme === 'dark' ? i18n.t('switchToLight') : i18n.t('switchToDark')}
                  onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark', false)}
                >
                  {theme === 'dark' ? (
                    // Sun icon
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M6.76 4.84l-1.8-1.79-1.41 1.41 1.79 1.8 1.42-1.42zm10.45-1.79l-1.8 1.79 1.42 1.42 1.79-1.8-1.41-1.41zM12 4V1h-1v3h1zm0 19v-3h-1v3h1zm8-8h3v-1h-3v1zM1 12H4v-1H1v1zm15.24 7.16l1.8 1.79 1.41-1.41-1.79-1.8-1.42 1.42zM4.84 17.24l-1.79 1.8 1.41 1.41 1.8-1.79-1.42-1.42zM12 7a5 5 0 100 10 5 5 0 000-10z"/>
                    </svg>
                  ) : (
                    // Moon icon
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/>
                    </svg>
                  )}
                </button>

                <select
                  id="signin-language"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value, false)}
                  className="select-compact"
                >
                  <option value="en">{i18n.t('langEN')}</option>
                  <option value="de">{i18n.t('langDE')}</option>
                </select>
              </div>
            </div>

            <h2>{i18n.t('signInTitle')}</h2>
            <p className="small" style={{marginTop: 0}}>{i18n.t('signInSubtitle')}</p>

            <div className="field">
              <label htmlFor="email">{i18n.t('jiraEmail')}</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder={i18n.t('emailPlaceholder')}
                required
              />
              <p className="small" style={{marginTop: 0}}>
                {i18n.t('snkOnlyNote')}
              </p>
            </div>

            <div className="field">
              <label htmlFor="token">{i18n.t('jiraToken')}</label>
              <input
                id="token"
                type="password"
                value={token}
                onChange={e => setToken(e.target.value)}
                placeholder={i18n.t('tokenPlaceholder')}
                required
              />
            </div>

            <div className="help-link-wrap">
              <a
                className="help-link"
                href="https://support.atlassian.com/atlassian-account/docs/manage-api-tokens-for-your-atlassian-account/"
                target="_blank"
              >
                <span className="help-link__icon" aria-hidden="true">
                  {/* external link icon */}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 3h7v7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                          strokeLinejoin="round"/>
                    <path d="M10 14L21 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                          strokeLinejoin="round"/>
                    <path d="M21 14v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h6" stroke="currentColor"
                          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                <span>How to generate an API token</span>
              </a>
            </div>

            {error && (
              <div className="error" role="alert">{error}</div>
            )}
            {message && (
              <div className="text-success" role="status">{message}</div>
            )}

            <button type="submit" disabled={disabled} className="btn primary btn-block">
              {isCheckingConnection ? i18n.t('connecting') : i18n.t('connect')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
